/* The one place this app talks to the station.
 *
 * Every call goes through `request()`, and `request()` has one rule: it either
 * returns a body that parsed cleanly, or it throws an ApiError. It never
 * returns half of something. A page that receives a value can render it; a page
 * that catches an ApiError knows what to say. There is no third case where a
 * component has to guess whether `results` being undefined means "empty" or
 * "the proxy is down", because that guess is always wrong somewhere.
 *
 * The 422 handling is the part that earns its keep. FastAPI answers a failed
 * validation with `detail: [{loc, msg, type}, …]`, and the submit form has to
 * put each of those messages under the input it belongs to — those messages are
 * written for a musician who has just spent an hour on a pitch (see the note at
 * the top of api/hgr/schemas.py), and dumping them all in one red box at the
 * top of the page throws away the work that went into them. So `loc` is reduced
 * to a field name and the messages come back keyed by it.
 *
 * Base is `/api`, which vite proxies to :8000 in dev and the reverse proxy
 * serves in production. Admin calls send the session cookie; public calls do
 * not need to and do not ask.
 */

import type {
  AboutPageContent,
  AboutPageUpdate,
  AdminStats,
  AdminSubmissionList,
  AdminSubmissionRow,
  ArtworkOut,
  ClearanceCreate,
  ClearanceOut,
  ChatIdentity,
  ChatAccount,
  ChatAccountCredentials,
  ChatActionInput,
  ChatDrinkInput,
  ChatDrop,
  ChatDropCreateInput,
  ChatHiddenOut,
  ChatJoinInput,
  ChatMessage,
  ChatMessageInput,
  ChatPositionInput,
  ChatPresence,
  ChatRelaxInput,
  ChatRoomAdminOut,
  ChatSnapshot,
  ChatSpaBouncerInput,
  ChatSpaDoorState,
  ChatSpaEntryDecisionInput,
  ChatSpaEntryRequestInput,
  ChatTattooInput,
  ChatWaterInput,
  RadioListeningHeartbeat,
  DeliveryOut,
  DeliveryRequest,
  EpisodeDetail,
  EpisodeList,
  EpisodeSort,
  GenreOut,
  HostCard,
  HostDetail,
  HostList,
  HostStatus,
  LiveState,
  LoginRequest,
  PreflightOut,
  PreflightRow,
  PremiereDetail,
  PremiereList,
  CueList,
  ScheduleResponse,
  SoundCloudSchedule,
  StaffOut,
  StudioEpisodeCreate,
  StudioEpisodeDetail,
  StudioEpisodeRow,
  StudioEpisodeUpdate,
  StudioFileList,
  StudioHostCreate,
  StudioHostDetail,
  StudioHostRow,
  StudioHostUpdate,
  StudioImportRequest,
  StudioImportResult,
  StudioPremiere,
  StudioPremiereCreate,
  StudioPremiereUpdate,
  StudioShowCreate,
  StudioShowDetail,
  StudioShowRow,
  StudioLiveState,
  StudioShowUpdate,
  SubmissionInput,
  SubmissionOut,
  SubmissionState,
  TransitionRequest,
} from './types';

const BASE = '/api';

/* ------------------------------------------------------------------- errors */

/**
 * Anything the API said no to, in one shape.
 *
 * `status` is the HTTP code, or 0 when the request never reached a server at
 * all — a distinction worth keeping, because "we are down" and "you are on a
 * train" want different words on screen.
 *
 * `fields` is empty except on a 422.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;
  readonly fields: Record<string, string>;

  constructor(status: number, detail: string, fields: Record<string, string> = {}) {
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.fields = fields;
    // Extending a builtin under a downlevel target loses the prototype chain,
    // and `err instanceof ApiError` silently becomes false in every catch block
    // in the app. One line, and it stays true.
    Object.setPrototypeOf(this, ApiError.prototype);
  }

  /** A field-keyed validation failure, i.e. the form can render itself. */
  get isValidation(): boolean {
    return this.status === 422 && Object.keys(this.fields).length > 0;
  }

  /** No session, or an expired one. The admin pages redirect on this. */
  get isUnauthorised(): boolean {
    return this.status === 401 || this.status === 403;
  }

  /** The request never reached the station. */
  get isOffline(): boolean {
    return this.status === 0;
  }
}

/**
 * True for the exception a cancelled fetch throws.
 *
 * An aborted request is not a failure — it is a component that navigated away,
 * or a search box whose next keystroke arrived first. Callers check this before
 * putting an error on screen, and abort errors are deliberately NOT wrapped in
 * ApiError so that a `catch` which forgets to check cannot show one.
 */
export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException
    ? error.name === 'AbortError'
    : error instanceof Error && error.name === 'AbortError';
}

/** What we say when the server did not say anything useful itself. */
function fallbackDetail(status: number): string {
  if (status === 0) return 'We could not reach the station. Check the connection and try again.';
  if (status === 401 || status === 403) return 'You are not signed in.';
  if (status === 404) return 'There is nothing at that address.';
  if (status === 429) return 'That is more requests than we allow. Give it a minute.';
  if (status >= 500) return 'The station API is having a problem. That one is ours, not yours.';
  return `The request failed (${status}).`;
}

interface ValidationItem {
  loc?: unknown[];
  msg?: string;
}

/**
 * `["body", "genre_slugs", 0]` -> `genre_slugs`.
 *
 * The last element of `loc` is the field, except when the failure is inside a
 * list, where the last element is the index. An error keyed `0` would be
 * rendered under nothing at all, so numeric tail elements are stepped over and
 * the last NAMED element wins. `body` alone is a whole-payload error and is
 * keyed as such.
 */
function fieldName(loc: unknown[] | undefined): string {
  if (!Array.isArray(loc) || loc.length === 0) return 'detail';
  for (let i = loc.length - 1; i >= 0; i -= 1) {
    const part = loc[i];
    if (typeof part === 'string' && part !== 'body') return part;
  }
  const last = loc[loc.length - 1];
  return typeof last === 'string' ? last : 'detail';
}

function parseValidation(items: ValidationItem[]): Record<string, string> {
  const fields: Record<string, string> = {};
  for (const item of items) {
    const key = fieldName(item?.loc);
    const message = typeof item?.msg === 'string' ? item.msg.trim() : '';
    if (!message) continue;
    // First message per field wins. Pydantic emits the specific rule before the
    // generic one, and the specific one is the one worth reading.
    if (!(key in fields)) fields[key] = message;
  }
  return fields;
}

function summarise(fields: Record<string, string>, status: number): string {
  const messages = Object.values(fields);
  if (messages.length === 1) return messages[0];
  if (messages.length > 1) return `${messages.length} fields need another look.`;
  return fallbackDetail(status);
}

async function toApiError(response: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // An HTML error page from a proxy, or an empty body. Neither is a reason to
    // throw a different, less useful error.
    return new ApiError(response.status, fallbackDetail(response.status));
  }

  const detail = (body as { detail?: unknown } | null)?.detail;

  if (Array.isArray(detail)) {
    const fields = parseValidation(detail as ValidationItem[]);
    return new ApiError(response.status, summarise(fields, response.status), fields);
  }
  if (typeof detail === 'string' && detail.trim()) {
    return new ApiError(response.status, detail.trim());
  }
  return new ApiError(response.status, fallbackDetail(response.status));
}

/* ------------------------------------------------------------------ request */

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /** Send the session cookie. Admin only. */
  auth?: boolean;
  /** Never serve /api/live out of a browser cache. */
  noStore?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, auth = false, noStore = false } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      headers,
      signal,
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: auth ? 'include' : 'same-origin',
      cache: noStore ? 'no-store' : 'default',
    });
  } catch (error) {
    // A cancelled request is the caller's own doing; it goes back untouched so
    // that `isAbortError` still recognises it.
    if (isAbortError(error)) throw error;
    throw new ApiError(0, fallbackDetail(0));
  }

  if (!response.ok) throw await toApiError(response);

  // 204, or a 200 with nothing in it. Callers that type this as void expect
  // undefined; callers that expect a body would rather have the parse error
  // below than a silent undefined.
  if (response.status === 204 || response.headers.get('Content-Length') === '0') {
    return undefined as T;
  }

  try {
    return (await response.json()) as T;
  } catch {
    throw new ApiError(
      response.status,
      'The station answered with something we could not read. Try again in a moment.',
    );
  }
}

/** Send a browser-selected file without inventing a JSON/base64 transport. */
async function multipartRequest<T>(
  path: string,
  body: FormData,
  signal?: AbortSignal,
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body,
      signal,
      credentials: 'include',
      cache: 'no-store',
    });
  } catch (error) {
    if (isAbortError(error)) throw error;
    throw new ApiError(0, fallbackDetail(0));
  }

  if (!response.ok) throw await toApiError(response);
  try {
    return (await response.json()) as T;
  } catch {
    throw new ApiError(
      response.status,
      'The station answered with something we could not read. Try again in a moment.',
    );
  }
}

/* ------------------------------------------------------------ query strings */

type QueryValue = string | number | boolean | readonly string[] | null | undefined;

/**
 * Only what was actually asked for.
 *
 * Empty values are dropped rather than sent as `?q=`: an empty search box is a
 * box someone just cleared, not a request for episodes whose title contains
 * nothing. Arrays repeat the key, which is what FastAPI reads back as a list.
 */
function query(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        const trimmed = String(item).trim();
        if (trimmed) search.append(key, trimmed);
      }
      continue;
    }
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) search.set(key, trimmed);
      continue;
    }
    search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : '';
}

/* --------------------------------------------------------------- parameters */

export interface ScheduleQuery {
  /** Station-local `YYYY-MM-DD`. Defaults to today in Madrid. */
  from?: string;
  /** 1..14. */
  days?: number;
}

export interface EpisodeQuery {
  /** Repeatable; an episode matching any of them is returned. */
  genre?: string | string[];
  host?: string;
  show?: string;
  staff_pick?: boolean;
  format?: 'live' | 'prerecorded';
  year?: number;
  /** Title, host, description and TRACKLIST. */
  q?: string;
  sort?: EpisodeSort;
  /** Default 24, max 96. */
  limit?: number;
  offset?: number;
  has_tracklist?: boolean;
  resident?: boolean;
}

export interface PremiereQuery {
  /**
   * A single genre slug. Singular, unlike `EpisodeQuery.genre`, because the
   * endpoint takes one — sending the key twice would leave FastAPI to pick, and
   * a filter whose result depends on which value the framework kept is not a
   * filter.
   */
  genre?: string;
  /**
   * True asks for the premieres that are still unreleased. LEFT OUT rather than
   * sent as false for "everything": `false` would be a request for the released
   * ones only, which is a third view nothing on the site offers.
   */
  unreleased?: boolean;
  /** Default 24, max 96 — the same page size as the archive. */
  limit?: number;
  offset?: number;
}

export interface HostQuery {
  resident?: boolean;
  /** A genre slug they have actually broadcast. */
  genre?: string;
  status?: HostStatus;
}

export interface AdminSubmissionQuery {
  state?: SubmissionState;
  /** Default 25, max 100. */
  limit?: number;
  offset?: number;
}

/* --------------------------------------------------------------------- api */

export const api = {
  /**
   * What is on air right now. Polled every 15s by the persistent player, which
   * is why it takes a signal — a poll that fires while the last one is still in
   * flight cancels its predecessor rather than racing it.
   */
  getLive(signal?: AbortSignal): Promise<LiveState> {
    return request<LiveState>('/live', { signal, noStore: true });
  },

  /** The current live room and its complete, bounded message window. */
  getChat(signal?: AbortSignal, present = false): Promise<ChatSnapshot> {
    return request<ChatSnapshot>(`/chat${present ? '?present=1' : ''}`, { signal, noStore: true });
  },

  /** The same signed-in identity used by every page, player and chat room. */
  getRadioAccount(signal?: AbortSignal): Promise<ChatAccount> {
    return request<ChatAccount>('/chat/account', { signal, noStore: true });
  },

  renameRadioAccount(username: string, signal?: AbortSignal): Promise<ChatAccount> {
    return request<ChatAccount>('/chat/account', {
      method: 'PATCH',
      body: { username },
      signal,
    });
  },

  /** Pick a public name for this browser's signed guest session. */
  joinChat(input: ChatJoinInput, signal?: AbortSignal): Promise<ChatIdentity> {
    return request<ChatIdentity>('/chat/join', { method: 'POST', body: input, signal });
  },

  /** Send to one explicit room; the nonce makes an intentional retry idempotent. */
  sendChatMessage(input: ChatMessageInput, signal?: AbortSignal): Promise<ChatMessage> {
    return request<ChatMessage>('/chat/messages', { method: 'POST', body: input, signal });
  },

  /** Persist a responsive percentage position and refresh floor presence. */
  moveChatPresence(input: ChatPositionInput, signal?: AbortSignal): Promise<ChatPresence> {
    return request<ChatPresence>('/chat/presence', { method: 'POST', body: input, signal });
  },

  /** Take a bottle from the bar, or finish the one currently being carried. */
  setChatWater(input: ChatWaterInput, signal?: AbortSignal): Promise<ChatIdentity> {
    return request<ChatIdentity>('/chat/water', { method: 'POST', body: input, signal });
  },

  /** Choose a drink from the indoor beach-club bar. */
  setChatDrink(input: ChatDrinkInput, signal?: AbortSignal): Promise<ChatIdentity> {
    return request<ChatIdentity>('/chat/drink', { method: 'POST', body: input, signal });
  },

  /** Sit down in the lounge, or return the profile to its standing pose. */
  setChatRelax(input: ChatRelaxInput, signal?: AbortSignal): Promise<ChatIdentity> {
    return request<ChatIdentity>('/chat/relax', { method: 'POST', body: input, signal });
  },

  /** Choose one of the code-native profile tattoos while inside the studio. */
  setChatTattoo(input: ChatTattooInput, signal?: AbortSignal): Promise<ChatIdentity> {
    return request<ChatIdentity>('/chat/tattoo', { method: 'POST', body: input, signal });
  },

  setChatAction(input: ChatActionInput, signal?: AbortSignal): Promise<ChatIdentity> {
    return request<ChatIdentity>('/chat/action', { method: 'POST', body: input, signal });
  },

  setSpaBouncer(input: ChatSpaBouncerInput, signal?: AbortSignal): Promise<ChatSpaDoorState> {
    return request<ChatSpaDoorState>('/chat/spa/bouncer', {
      method: 'POST',
      body: input,
      signal,
    });
  },

  requestSpaEntry(
    input: ChatSpaEntryRequestInput,
    signal?: AbortSignal,
  ): Promise<ChatSpaDoorState> {
    return request<ChatSpaDoorState>('/chat/spa/requests', {
      method: 'POST',
      body: input,
      signal,
    });
  },

  decideSpaEntry(
    requestId: string,
    input: ChatSpaEntryDecisionInput,
    signal?: AbortSignal,
  ): Promise<ChatSpaDoorState> {
    return request<ChatSpaDoorState>(
      `/chat/spa/requests/${encodeURIComponent(requestId)}/decision`,
      { method: 'POST', body: input, signal },
    );
  },

  registerChatAccount(
    input: ChatAccountCredentials,
    signal?: AbortSignal,
  ): Promise<ChatAccount> {
    return request<ChatAccount>('/chat/account/register', { method: 'POST', body: input, signal });
  },

  loginChatAccount(input: ChatAccountCredentials, signal?: AbortSignal): Promise<ChatAccount> {
    return request<ChatAccount>('/chat/account/login', { method: 'POST', body: input, signal });
  },

  async logoutChatAccount(signal?: AbortSignal): Promise<void> {
    await request<unknown>('/chat/account/logout', { method: 'POST', signal });
  },

  recordRadioListening(
    input: RadioListeningHeartbeat,
    signal?: AbortSignal,
  ): Promise<ChatAccount> {
    return request<ChatAccount>('/chat/account/listening', {
      method: 'POST',
      body: input,
      signal,
    });
  },

  claimChatDrop(dropId: string, signal?: AbortSignal): Promise<ChatAccount> {
    return request<ChatAccount>(`/chat/drops/${encodeURIComponent(dropId)}/claim`, {
      method: 'POST',
      signal,
    });
  },

  getAboutPage(signal?: AbortSignal): Promise<AboutPageContent> {
    return request<AboutPageContent>('/pages/about', { signal });
  },

  getSchedule(params: ScheduleQuery = {}, signal?: AbortSignal): Promise<ScheduleResponse> {
    return request<ScheduleResponse>(
      `/schedule${query({ from: params.from, days: params.days })}`,
      { signal },
    );
  },

  /** The .ics for one booking. A URL, not a fetch: the browser downloads it. */
  icsUrl(catalogueNo: string): string {
    return `${BASE}/schedule/${encodeURIComponent(catalogueNo.toUpperCase())}.ics`;
  },

  getEpisodes(params: EpisodeQuery = {}, signal?: AbortSignal): Promise<EpisodeList> {
    return request<EpisodeList>(
      `/episodes${query({
        genre: typeof params.genre === 'string' ? [params.genre] : params.genre,
        host: params.host,
        show: params.show,
        staff_pick: params.staff_pick,
        format: params.format,
        year: params.year,
        q: params.q,
        sort: params.sort,
        limit: params.limit,
        offset: params.offset,
        has_tracklist: params.has_tracklist,
        resident: params.resident,
      })}`,
      { signal },
    );
  },

  getEpisode(catalogueNo: string, signal?: AbortSignal): Promise<EpisodeDetail> {
    return request<EpisodeDetail>(`/episodes/${encodeURIComponent(catalogueNo.toUpperCase())}`, {
      signal,
    });
  },

  /** The shared public SoundCloud programme for the off-air station player. */
  getSoundCloudSchedule(signal?: AbortSignal): Promise<SoundCloudSchedule> {
    return request<SoundCloudSchedule>('/episodes/soundcloud-schedule', { signal });
  },

  /**
   * The premieres feed, newest first.
   *
   * A separate endpoint rather than `/episodes?kind=premiere`, and the reason is
   * D14 rather than tidiness: an episode is a two-hour broadcast whose recording
   * side needs its own clearance, and a premiere is one unreleased track whose
   * performers consented directly. Different objects, different licence
   * reasoning, different cards. See the note above `PremiereCard` in types.ts.
   */
  getPremieres(params: PremiereQuery = {}, signal?: AbortSignal): Promise<PremiereList> {
    return request<PremiereList>(
      `/premieres${query({
        genre: params.genre,
        unreleased: params.unreleased,
        limit: params.limit,
        offset: params.offset,
      })}`,
      { signal },
    );
  },

  getPremiere(slug: string, signal?: AbortSignal): Promise<PremiereDetail> {
    return request<PremiereDetail>(`/premieres/${encodeURIComponent(slug)}`, { signal });
  },

  /** The roster. Archived hosts are returned too, tagged; they are still people. */
  async getHosts(params: HostQuery = {}, signal?: AbortSignal): Promise<HostCard[]> {
    const body = await request<HostList>(
      `/hosts${query({ resident: params.resident, genre: params.genre, status: params.status })}`,
      { signal },
    );
    return body.results ?? [];
  },

  getHost(slug: string, signal?: AbortSignal): Promise<HostDetail> {
    return request<HostDetail>(`/hosts/${encodeURIComponent(slug)}`, { signal });
  },

  /**
   * The whole vocabulary, including the genres with no episodes — the archive
   * needs the counts to decide which chips to draw, and the submit form needs
   * every genre whether or not anyone has broadcast it yet.
   */
  getGenres(signal?: AbortSignal): Promise<GenreOut[]> {
    return request<GenreOut[]>('/genres', { signal });
  },

  /** The open call. Throws an ApiError whose `fields` the form renders inline. */
  postSubmission(body: SubmissionInput, signal?: AbortSignal): Promise<SubmissionOut> {
    return request<SubmissionOut>('/submissions', { method: 'POST', body, signal });
  },

  /* --------------------------------------------------------------- admin */

  adminLogin(body: LoginRequest, signal?: AbortSignal): Promise<StaffOut> {
    return request<StaffOut>('/admin/login', { method: 'POST', body, signal, auth: true });
  },

  async adminLogout(signal?: AbortSignal): Promise<void> {
    await request<unknown>('/admin/logout', { method: 'POST', signal, auth: true });
  },

  /** Who is signed in. A 401 here is the whole session check. */
  adminMe(signal?: AbortSignal): Promise<StaffOut> {
    return request<StaffOut>('/admin/me', { signal, auth: true, noStore: true });
  },

  setChatRoomEnabled(
    catalogueNo: string,
    enabled: boolean,
    signal?: AbortSignal,
  ): Promise<ChatRoomAdminOut> {
    return request<ChatRoomAdminOut>(
      `/admin/chat/rooms/${encodeURIComponent(catalogueNo.toUpperCase())}`,
      { method: 'PATCH', body: { enabled }, signal, auth: true },
    );
  },

  hideChatMessage(
    messageId: number,
    reason: string | null = null,
    signal?: AbortSignal,
  ): Promise<ChatHiddenOut> {
    return request<ChatHiddenOut>(`/admin/chat/messages/${messageId}/hide`, {
      method: 'POST',
      body: { reason },
      signal,
      auth: true,
    });
  },

  startChatDrop(input: ChatDropCreateInput, signal?: AbortSignal): Promise<ChatDrop> {
    return request<ChatDrop>('/admin/chat/drops', {
      method: 'POST',
      body: input,
      signal,
      auth: true,
    });
  },

  updateChatDrop(dropId: string, enabled: boolean, signal?: AbortSignal): Promise<ChatDrop> {
    return request<ChatDrop>(`/admin/chat/drops/${encodeURIComponent(dropId)}`, {
      method: 'PATCH',
      body: { enabled },
      signal,
      auth: true,
    });
  },

  adminSubmissions(
    params: AdminSubmissionQuery = {},
    signal?: AbortSignal,
  ): Promise<AdminSubmissionList> {
    return request<AdminSubmissionList>(
      `/admin/submissions${query({
        state: params.state,
        limit: params.limit,
        offset: params.offset,
      })}`,
      { signal, auth: true, noStore: true },
    );
  },

  /**
   * Move a submission. The server validates against the state machine and
   * writes an audit row, so the queue only ever offers the moves listed in the
   * row's own `allowed_transitions`.
   */
  adminTransition(
    id: string,
    body: TransitionRequest,
    signal?: AbortSignal,
  ): Promise<AdminSubmissionRow> {
    return request<AdminSubmissionRow>(
      `/admin/submissions/${encodeURIComponent(id)}/transition`,
      { method: 'POST', body, signal, auth: true },
    );
  },

  /** Bookings the reminder chain would silently skip. Empty is the good case. */
  async adminPreflight(signal?: AbortSignal): Promise<PreflightRow[]> {
    const body = await request<PreflightOut>('/admin/preflight', {
      signal,
      auth: true,
      noStore: true,
    });
    return body.results ?? [];
  },

  adminStats(signal?: AbortSignal): Promise<AdminStats> {
    return request<AdminStats>('/admin/stats', { signal, auth: true, noStore: true });
  },

  /* The cue desk. noStore because a master must not sit in any cache, which is
   * the same reason the audio endpoint sends `Cache-Control: private, no-store`. */
  adminCue(signal?: AbortSignal): Promise<CueList> {
    return request<CueList>('/admin/cue', { signal, auth: true, noStore: true });
  },

  /* -------------------------------------------------------------- studio
   *
   * The operator's desk (pages/Studio.tsx). Everything below is
   * `auth: true, noStore: true` for the same two reasons every admin call is:
   * the session cookie has to go, and a desk that edits rows must never read a
   * cached copy of the row it is about to patch.
   *
   * EVERY PATCH TAKES A PARTIAL AND SENDS EXACTLY WHAT IT WAS GIVEN. The
   * handlers read `model_dump(exclude_unset=True)`, so a key that is absent
   * means "leave alone" and an explicit null means "clear it". The desk
   * therefore builds a patch out of the fields the operator actually touched —
   * which is what makes a second open tab unable to revert a field it never
   * looked at. There is no version column and no 409 behind this, so the
   * discipline is the whole mechanism. See the studio block in types.ts.
   */

  studioEpisodes(signal?: AbortSignal): Promise<StudioEpisodeRow[]> {
    return request<StudioEpisodeRow[]>('/admin/studio/episodes', {
      signal,
      auth: true,
      noStore: true,
    });
  },

  studioAbout(signal?: AbortSignal): Promise<AboutPageContent> {
    return request<AboutPageContent>('/admin/studio/about', {
      signal,
      auth: true,
      noStore: true,
    });
  },

  studioPatchAbout(
    body: AboutPageUpdate,
    signal?: AbortSignal,
  ): Promise<AboutPageContent> {
    return request<AboutPageContent>('/admin/studio/about', {
      method: 'PATCH',
      body,
      signal,
      auth: true,
      noStore: true,
    });
  },

  /**
   * One episode, with the editable columns and what is on disk for it.
   *
   * The catalogue number is upper-cased on the way in, the same as
   * `getEpisode` — `hgr-042` and `HGR-042` are the same episode, and the desk
   * should not be the thing that decides otherwise.
   */
  studioEpisode(catalogueNo: string, signal?: AbortSignal): Promise<StudioEpisodeDetail> {
    return request<StudioEpisodeDetail>(
      `/admin/studio/episodes/${encodeURIComponent(catalogueNo.toUpperCase())}`,
      { signal, auth: true, noStore: true },
    );
  },

  studioCreateEpisode(
    body: StudioEpisodeCreate,
    signal?: AbortSignal,
  ): Promise<StudioEpisodeDetail> {
    return request<StudioEpisodeDetail>('/admin/studio/episodes', {
      method: 'POST',
      body,
      signal,
      auth: true,
      noStore: true,
    });
  },

  studioPatchEpisode(
    catalogueNo: string,
    body: StudioEpisodeUpdate,
    signal?: AbortSignal,
  ): Promise<StudioEpisodeDetail> {
    return request<StudioEpisodeDetail>(
      `/admin/studio/episodes/${encodeURIComponent(catalogueNo.toUpperCase())}`,
      { method: 'PATCH', body, signal, auth: true, noStore: true },
    );
  },

  /**
   * Prepare a delivery copy, and publish it if the tier allows.
   *
   * THIS REQUEST STAYS OPEN FOR THE WHOLE TRANSCODE. There is no job queue
   * behind it: the handler measures, applies one linear gain and writes the
   * delivery while the socket is held, which on a four-hour set is minutes. No
   * signal is passed by the desk on purpose — aborting the fetch would abandon
   * the answer, not the work.
   *
   * `publish: false` prepares without touching the public directory, which is
   * what you want when checking a level before deciding anything.
   */
  studioDelivery(
    catalogueNo: string,
    body: DeliveryRequest,
    signal?: AbortSignal,
  ): Promise<DeliveryOut> {
    return request<DeliveryOut>(
      `/admin/studio/episodes/${encodeURIComponent(catalogueNo.toUpperCase())}/delivery`,
      { method: 'POST', body, signal, auth: true, noStore: true },
    );
  },

  /**
   * Take the audio off the site.
   *
   * Answers with a `DeliveryOut` — the same shape as preparing one — because
   * unpublishing is the other half of that operation: the public file goes and
   * the column is cleared, while the staged copy stays so putting it back needs
   * no re-encode. It is NOT the episode detail, so the desk refetches the row
   * afterwards rather than rendering from this body.
   */
  studioUnpublish(catalogueNo: string, signal?: AbortSignal): Promise<DeliveryOut> {
    return request<DeliveryOut>(
      `/admin/studio/episodes/${encodeURIComponent(catalogueNo.toUpperCase())}/unpublish`,
      { method: 'POST', signal, auth: true, noStore: true },
    );
  },

  studioHosts(signal?: AbortSignal): Promise<StudioHostRow[]> {
    return request<StudioHostRow[]>('/admin/studio/hosts', {
      signal,
      auth: true,
      noStore: true,
    });
  },

  studioHost(slug: string, signal?: AbortSignal): Promise<StudioHostDetail> {
    return request<StudioHostDetail>(`/admin/studio/hosts/${encodeURIComponent(slug)}`, {
      signal,
      auth: true,
      noStore: true,
    });
  },

  studioCreateHost(body: StudioHostCreate, signal?: AbortSignal): Promise<StudioHostDetail> {
    return request<StudioHostDetail>('/admin/studio/hosts', {
      method: 'POST',
      body,
      signal,
      auth: true,
      noStore: true,
    });
  },

  studioPatchHost(
    slug: string,
    body: StudioHostUpdate,
    signal?: AbortSignal,
  ): Promise<StudioHostDetail> {
    return request<StudioHostDetail>(`/admin/studio/hosts/${encodeURIComponent(slug)}`, {
      method: 'PATCH',
      body,
      signal,
      auth: true,
      noStore: true,
    });
  },

  studioShows(signal?: AbortSignal): Promise<StudioShowRow[]> {
    return request<StudioShowRow[]>('/admin/studio/shows', {
      signal,
      auth: true,
      noStore: true,
    });
  },

  studioShow(slug: string, signal?: AbortSignal): Promise<StudioShowDetail> {
    return request<StudioShowDetail>(`/admin/studio/shows/${encodeURIComponent(slug)}`, {
      signal,
      auth: true,
      noStore: true,
    });
  },

  studioCreateShow(body: StudioShowCreate, signal?: AbortSignal): Promise<StudioShowDetail> {
    return request<StudioShowDetail>('/admin/studio/shows', {
      method: 'POST',
      body,
      signal,
      auth: true,
      noStore: true,
    });
  },

  studioPatchShow(
    slug: string,
    body: StudioShowUpdate,
    signal?: AbortSignal,
  ): Promise<StudioShowDetail> {
    return request<StudioShowDetail>(`/admin/studio/shows/${encodeURIComponent(slug)}`, {
      method: 'PATCH',
      body,
      signal,
      auth: true,
      noStore: true,
    });
  },

  /** Recorded grants. Admin only — `grantor_email` is on this shape. */
  studioClearances(signal?: AbortSignal): Promise<ClearanceOut[]> {
    return request<ClearanceOut[]>('/admin/studio/clearances', {
      signal,
      auth: true,
      noStore: true,
    });
  },

  studioCreateClearance(body: ClearanceCreate, signal?: AbortSignal): Promise<ClearanceOut> {
    return request<ClearanceOut>('/admin/studio/clearances', {
      method: 'POST',
      body,
      signal,
      auth: true,
      noStore: true,
    });
  },

  /**
   * What is on disk under `root`, with the filename parser's guess attached.
   *
   * `root` is left out when the desk has not chosen one yet: the endpoint answers
   * with its default root and the list of roots it will accept, which is how the
   * browser learns what it is allowed to look at.
   */
  /**
   * What is on disk under an allowed root.
   *
   * `kind` matters: the default lists AUDIO by the importer's suffixes and its
   * 5 MiB floor, and 'image' lists what hgr/images.py can decode with no size
   * floor at all. The artwork picker used to call this without a kind, so the
   * only files it could ever offer were the ones the artwork endpoint refuses.
   */
  studioFiles(
    root?: string,
    kind?: 'audio' | 'image',
    signal?: AbortSignal,
  ): Promise<StudioFileList> {
    return request<StudioFileList>(`/admin/studio/files${query({ root, kind })}`, {
      signal,
      auth: true,
      noStore: true,
    });
  },

  /**
   * Turn files on disk into episodes. Nothing is copied and nothing is
   * published: an imported episode is a draft, and it is `uncleared` because
   * that is the state a recording is in before anybody has said anything about
   * it. Both are one field away on the exposure panel.
   */
  studioImport(body: StudioImportRequest, signal?: AbortSignal): Promise<StudioImportResult> {
    return request<StudioImportResult>('/admin/studio/import', {
      method: 'POST',
      body,
      signal,
      auth: true,
      noStore: true,
    });
  },

  /**
   * Render artwork for one row from a file on disk.
   *
   * `kind` is the collection segment the route takes, so the four callers cannot
   * assemble a path the API does not serve. `ident` is whatever that collection
   * is addressed by: a catalogue number for an episode, a slug for a host, a
   * show or a premiere.
   *
   * A 422 here is worth showing verbatim — the server's own message names the way
   * out for a HEIC or a CMYK TIFF, and it is better written than a rephrasing.
   */
  studioSetArtwork(
    kind: 'episodes' | 'premieres' | 'hosts' | 'shows',
    ident: string,
    path: string,
    signal?: AbortSignal,
  ): Promise<ArtworkOut> {
    return request<ArtworkOut>(
      `/admin/studio/${kind}/${encodeURIComponent(ident)}/artwork`,
      { method: 'POST', body: { path }, signal, auth: true, noStore: true },
    );
  },

  /** Upload a resident profile photo selected on this device. */
  studioUploadHostArtwork(
    slug: string,
    file: File,
    signal?: AbortSignal,
  ): Promise<ArtworkOut> {
    const body = new FormData();
    body.append('file', file, file.name);
    return multipartRequest<ArtworkOut>(
      `/admin/studio/hosts/${encodeURIComponent(slug)}/artwork/upload`,
      body,
      signal,
    );
  },

  /**
   * Clear a row's artwork and delete the renditions written for it.
   */
  async studioClearArtwork(
    kind: 'episodes' | 'premieres' | 'hosts' | 'shows',
    ident: string,
    signal?: AbortSignal,
  ): Promise<void> {
    await request<unknown>(`/admin/studio/${kind}/${encodeURIComponent(ident)}/artwork`, {
      method: 'DELETE',
      signal,
      auth: true,
      noStore: true,
    });
  },

  /* ------------------------------------------------------ admin premieres
   *
   * NOT a studio route: premieres were editable from `/api/admin/premieres`
   * before the desk existed, and one surface for them is better than two that
   * can disagree about an embargo. The list is unpaged on purpose — it is a
   * working queue of tens of rows, and the public endpoint is the one that pages.
   *
   * The studio sees the real `audio_url` of a premiere that is not public yet,
   * deliberately: the only other moment to notice the wrong file is attached is
   * after it has gone out.
   */

  adminPremieres(signal?: AbortSignal): Promise<StudioPremiere[]> {
    return request<StudioPremiere[]>('/admin/premieres', {
      signal,
      auth: true,
      noStore: true,
    });
  },

  adminCreatePremiere(
    body: StudioPremiereCreate,
    signal?: AbortSignal,
  ): Promise<StudioPremiere> {
    return request<StudioPremiere>('/admin/premieres', {
      method: 'POST',
      body,
      signal,
      auth: true,
      noStore: true,
    });
  },

  /**
   * Edit a premiere — everything except its slug, which is the URL a label
   * already pasted into its own newsletter.
   *
   * `id` is the row's UUID. See the note above `StudioPremiere` in types.ts:
   * today's serialiser does not return one, so the desk treats an id as a
   * capability rather than assuming it has one.
   */
  adminPatchPremiere(
    id: string,
    body: StudioPremiereUpdate,
    signal?: AbortSignal,
  ): Promise<StudioPremiere> {
    return request<StudioPremiere>(`/admin/premieres/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body,
      signal,
      auth: true,
      noStore: true,
    });
  },

  /* ------------------------------------------------------------- going live
   *
   * The go-live panel (pages/studio/GoLive.tsx). Read-only, and the only admin
   * read that talks to the broadcast box: the server asks Icecast and Liquidsoap
   * with short, bounded timeouts and reports what they said, so a chain that is
   * entirely absent — docker not running, which is the normal case on a laptop —
   * comes back as a 200 describing that rather than as a hung request.
   *
   * `noStore` matters more here than anywhere else on the desk. Every field is a
   * fact about this minute: whether a slot is open, whether a source is on
   * /live. A cached copy of this page is a wrong answer with a timestamp on it.
   */
  studioGoLive(signal?: AbortSignal): Promise<StudioLiveState> {
    return request<StudioLiveState>('/admin/studio/live', {
      signal,
      auth: true,
      noStore: true,
    });
  },
};

export type Api = typeof api;
